import { useEffect, useRef, useState } from "react";
import {
  BarChart3,
  Circle,
  ContactRound,
  CreditCard,
  FilePenLine,
  Image,
  LayoutDashboard,
  Landmark,
  ShoppingCart,
  TrendingUp,
  Mail,
  MessageCircle,
  MessageSquareText,
  MonitorCog,
  Package,
  PanelLeftClose,
  PanelLeftOpen,
  PanelsTopLeft,
  Plug,
  ReceiptText,
  Repeat,
  ScrollText,
  Settings,
  ShieldCheck,
  ShoppingBag,
  Star,
  UserRoundCog,
  UsersRound,
  Webhook,
  Warehouse,
  Wrench,
} from "lucide-react";
import yanshLogo from "../../assets/yansh-logo.png";

const ICONS = {
  dashboard: LayoutDashboard,
  leads: UsersRound,
  repairs: Wrench,
  services: Settings,
  products: Package,
  inventory: Warehouse,
  orders: ShoppingBag,
  whatsapp: MessageCircle,
  whatsappTemplates: MessageSquareText,
  customers: ContactRound,
  emailSettings: Mail,
  deviceMasters: MonitorCog,
  paymentAccounts: Landmark,
  webhooks: Webhook,
  auditLog: ScrollText,
  metaIntegration: Plug,
  hero: Image,
  popups: PanelsTopLeft,
  reviews: Star,
  billing: CreditCard,
  estimates: FilePenLine,
  purchases: ShoppingCart,
  profitLoss: TrendingUp,
  creditNotes: ReceiptText,
  recurringInvoices: Repeat,
  users: UserRoundCog,
  reports: BarChart3,
  warranty: ShieldCheck,
};

// One group per kind of work. Every page that can show in the navigation is listed here; a page missing from the list would fall into "Other".
const NAV_GROUPS = [
  { id: "platform", label: "Platform", items: ["vendorProfiles", "vendorUsers"] },
  { id: "operations", label: "Operations", items: ["dashboard", "reports", "profitLoss", "leads", "repairs", "customers", "warranty"] },
  { id: "billing", label: "Billing", items: ["estimates", "billing", "purchases", "creditNotes", "recurringInvoices"] },
  { id: "stock-catalog", label: "Stock & Catalog", items: ["inventory", "products", "services", "orders"] },
  { id: "engagement", label: "Engagement", items: ["whatsapp", "reviews"] },
  { id: "website-management", label: "Website Management", items: ["hero", "popups"] },
  { id: "settings", label: "Settings", items: ["whatsappTemplates", "metaIntegration", "emailSettings", "webhooks", "paymentAccounts", "deviceMasters", "auditLog"] },
  { id: "administration", label: "Administration", items: ["users"] },
];

export default function CRMSidebar({ currentPage, items = [], onNavigate }) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const mobileTriggerRef = useRef(null);
  const sidebarRef = useRef(null);
  useEffect(() => setMobileOpen(false), [currentPage]);
  useEffect(() => {
    document.body.classList.toggle("admin-nav-open", mobileOpen);
    return () => document.body.classList.remove("admin-nav-open");
  }, [mobileOpen]);
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const sidebar = sidebarRef.current;
    const focusableSelector = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';
    const focusable = () => [...(sidebar?.querySelectorAll(focusableSelector) || [])];
    const focusFrame = requestAnimationFrame(() => focusable()[0]?.focus());
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        setMobileOpen(false);
        return;
      }
      if (event.key !== "Tab") return;
      const controls = focusable();
      if (!controls.length) return;
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      mobileTriggerRef.current?.focus();
    };
  }, [mobileOpen]);
  const itemMap = new Map(items.map((item) => [item.id, item]));
  const groupedIds = new Set(NAV_GROUPS.flatMap((group) => group.items));
  const groups = [
    ...NAV_GROUPS.map((group) => ({
      ...group,
      items: group.items.map((id) => itemMap.get(id)).filter(Boolean),
    })).filter((group) => group.items.length),
    {
      id: "other",
      label: "Other",
      items: items.filter((item) => !groupedIds.has(item.id)),
    },
  ].filter((group) => group.items.length);

  return (
    <>
    <button ref={mobileTriggerRef} type="button" className="mobile-nav-trigger" onClick={() => setMobileOpen(true)} aria-label="Open navigation" aria-expanded={mobileOpen} aria-controls="admin-navigation">
      <PanelLeftOpen size={20} />
    </button>
    {mobileOpen && <button type="button" className="mobile-nav-backdrop" onClick={() => setMobileOpen(false)} aria-label="Close navigation" />}
    <aside ref={sidebarRef} id="admin-navigation" className={`crm-sidebar ${isCollapsed ? "collapsed" : "expanded"}${mobileOpen ? " mobile-open" : ""}`}>
      <div className="sidebar-header">
        <button
          type="button"
          className="sidebar-toggle"
          onClick={() => mobileOpen ? setMobileOpen(false) : setIsCollapsed((current) => !current)}
          aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
        </button>
        <div className="sidebar-brand" aria-hidden={isCollapsed}>
          <img className="brand-logo" src={yanshLogo} alt="Yansh Technologies" />
        </div>
      </div>

      <nav className="sidebar-nav" aria-label="Main navigation">
        {groups.map((group) => (
          <section className="nav-section" key={group.id} aria-label={group.label}>
            <p className="nav-section-label">{group.label}</p>
            <ul className="nav-list">
              {group.items.map((item) => {
                const Icon = ICONS[item.id] || Circle;
                const active = currentPage === item.id;
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      className={`nav-item ${active ? "active" : ""}${item.disabled ? " requires-backend" : ""}`}
                      onClick={() => !item.disabled && onNavigate(item.id)}
                      title={item.disabled ? `${item.label} is not available yet` : item.label}
                      data-label={item.label}
                      aria-current={active ? "page" : undefined}
                      disabled={item.disabled}
                    >
                      <span className="nav-icon">
                        <Icon size={18} strokeWidth={2} aria-hidden="true" />
                      </span>
                      <span className="nav-label">{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </nav>
    </aside>
    </>
  );
}
